import { useEffect, useState } from "react";
import { Calendar, MapPin, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";

const fmt = (d) => {
  if (!d) return "Coming soon";
  try {
    return new Date(d).toLocaleDateString("nl-BE", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return d;
  }
};

const EventCard = ({ e, i, past }) => (
  <Reveal delay={(i % 2) * 0.08}>
    <div className={`group border border-black/10 bg-white overflow-hidden ${past ? "opacity-90" : ""}`} data-testid={`event-card-${i}`}>
      {e.image_url && (
        <div className="relative aspect-[16/9] overflow-hidden bg-[#EEF1F7]">
          <img src={e.image_url} alt={e.title} className={`h-full w-full object-cover transition-transform duration-700 group-hover:scale-105 ${past ? "grayscale" : ""}`} />
          <span className="absolute top-4 left-4 text-xs font-600 uppercase tracking-wider px-3 py-1.5 rounded-full text-white" style={{ background: past ? "rgba(11,11,12,0.55)" : "var(--elsa-blue)" }}>
            {past ? "Voorbij" : "Aankomend"}
          </span>
        </div>
      )}
      <div className="p-7">
        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-3">
          <span className="flex items-center gap-1.5"><Calendar size={15} style={{ color: "var(--elsa-blue)" }} /> {fmt(e.date)}</span>
          <span className="flex items-center gap-1.5"><MapPin size={15} style={{ color: "var(--elsa-blue)" }} /> {e.location}</span>
        </div>
        <h3 className="font-serif text-2xl md:text-3xl text-[#0B0B0C] mb-2">{e.title}</h3>
        <p className="text-muted-foreground text-balance">{e.description}</p>
        {!past && e.register_url && (
          <a href={e.register_url} className="inline-flex items-center gap-1.5 mt-5 text-sm font-600 text-[#0A3087] hover:text-[#0A3087] transition-colors">
            Inschrijven <ArrowUpRight size={16} />
          </a>
        )}
      </div>
    </div>
  </Reveal>
);

export default function Events() {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  const upcoming = events.filter((e) => e.type === "upcoming");
  const past = events.filter((e) => e.type === "past");

  return (
    <div data-testid="events-page">
      <PageHero
        eyebrow="Wat er te beleven valt"
        title={<>Onze<br />events.</>}
        subtitle="Van moot courts tot netwerkavonden — ontdek wat ELSA Brussel dit jaar voor je in petto heeft."
        testid="events-hero"
      />
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20">
        <h2 className="font-serif text-3xl md:text-4xl text-[#0B0B0C] mb-10">Aankomende events</h2>
        <div className="grid md:grid-cols-2 gap-8">
          {upcoming.map((e, i) => <EventCard key={e.id} e={e} i={i} />)}
        </div>
        {upcoming.length === 0 && <p className="text-muted-foreground">Nog geen aankomende events gepland.</p>}

        {past.length > 0 && (
          <>
            <h2 className="font-serif text-3xl md:text-4xl text-[#0B0B0C] mt-24 mb-10">Voorbije events</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {past.map((e, i) => <EventCard key={e.id} e={e} i={i} past />)}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
